import { on, TanokDispatcher, rethrowFx, throttle, debounce } from 'tanok';
import imm from 'immutable';
import {
    getRoom,
    BESIDA_ROLE_BUYER,
    BESIDA_ROLE_COMPANY,
    CONTEXT_TYPE_PRODUCT,
    ERROR_TYPE_STOP_WORD,
    ERROR_TYPE_CAPS_LOCK,
    ERROR_UNIQUE_USERS,
    BESIDA_TYPING_DELAY,
    MESSAGE_STATUS_SENDING,
    BESIDA_EVENT_START_TYPING
} from '@evo/chat-core';

import { Config } from './../../core/config';
import * as c from '../../libs/constants';
import * as a from '../../libs/actions';
import * as h from '../../libs/helpers';
import * as u from '../../libs/utils';
import * as fx from '../../libs/effect';
import * as agg from '../../libs/aggregation';

export class WebsocketDispatcher extends TanokDispatcher {
    @on(a.EVENT_INIT)
    init(_, state) {
        let updState = state;

        updState = updState.set(
            'chatHeightCoefficient',
            u.countChatHeightCoefficient(updState.get('windowHeight'))
        );

        return [
            updState,
            fx.connectBesidaFx(
                state.get('userRole'),
                state.get('chatsStatus'),
                state.get('userHasCompany'),
                state.get('userHasC2CCompany'))
        ];
    }

    @on(a.EVENT_OPEN_DIALOG)
    openDialog(payload, state) {
        if (!state.get('besida')) return [state];

        let updState = state;
        const result = [];

        const analyticsAction = u.getStartButtonAnalyticsAction(payload);
        const startButtonTheme = u.getStartButtonTheme(payload) || (payload && payload.theme);
        const contextItem = u.getStartButtonContext(payload) || payload;

        if (contextItem && contextItem.contextItemId) {
            updState = updState.set('contextItemId', parseInt(contextItem.contextItemId, 10));
            updState = updState.set('contextItemType', contextItem.contextItemType);
            updState = updState.set('contextItemPrice', contextItem.contextItemPrice);
            updState = updState.set('contextItemCurrency', contextItem.contextItemCurrency);
            updState = updState.set('contextDescription', contextItem.contextDescription);
            updState = updState.set('contextMeta', contextItem.contextMeta);
        }
        if (analyticsAction) {
            const data = { action: analyticsAction, params: {} };
            if (updState.get('userId')) data.params.auth = true;
            else data.params.noauth = true;
            updState = updState.setIn(['analyticsQueue', 'openDialog'], data);
        }

        updState = updState.set('visible', true);

        if (!state.get('userId') || !payload) {
            result.unshift(updState);
            return result;
        }

        if (startButtonTheme || updState.get('autoKnockKnock')) {
            const roomTheme = startButtonTheme || updState.get('autoKnockKnock');
            updState = updState.set('theme', roomTheme);

            const findChatByTheme = (chat) => chat.get('theme') === roomTheme;
            const roomOpenedBefore = u.getSocketStore(updState).get('chats').find(findChatByTheme);

            if (roomOpenedBefore) {
                result.push(fx.openRoomFx(roomOpenedBefore.get('room_ident')));
                result.unshift(updState);
                return result;
            }

            result.push(fx.besidaKnockKnockFx(updState.get('besida'), roomTheme));
            result.unshift(updState);
            return result;
        }

        if (!u.hasCompanyRole(updState) && !updState.get('autoKnockKnock') &&
            (u.getSocketStore(state).get('chats') || imm.List()).size === 1 &&
            !updState.getIn(['launchOnce', 'openRoom'])
        ) {
            updState = updState.setIn(['launchOnce', 'openRoom'], true);
            result.push(fx.openRoomFx(
                u.getSocketStore(updState).get('chats').first().get('room_ident')));
            result.unshift(updState);
            return result;
        }
        result.unshift(updState);
        return result;
    }

    @on(a.EVENT_BACK_TO_CHAT_LIST)
    backToChatList({ newMsgInCurrentDialog, newMsgMarkShown }, state) {
        const { IS_SUDO_MODE, IS_DEBUG } = Config.data.appState;

        let updState = state;
        const result = [];
        const deleteRoomErrors = (updState.get('errors') || imm.Map())
            .delete(ERROR_TYPE_STOP_WORD)
            .delete(ERROR_TYPE_CAPS_LOCK)
            .delete(ERROR_UNIQUE_USERS);
        updState = updState.set('errors', deleteRoomErrors);
        const currentRoom = getRoom(
            u.getSocketStore(updState), updState.get('openedRoomIdent'));

        updState = updState.set('currentRoomName', null);
        updState = updState.set('openedRoomIdent', null);
        updState = updState.set('stayInDialog', false);
        updState = updState.set('newMsgMarkShown', false);

        if (IS_SUDO_MODE && !IS_DEBUG) return [updState];

        if (newMsgInCurrentDialog && newMsgMarkShown) {
            const lastUnreadMsg = u.findLastUnreadMsg(
                currentRoom.get('history'),
                currentRoom.get('last_read_counter')
            );

            lastUnreadMsg && result.push(
                fx.besidaMarkMsgReadFx(
                    updState.get('besida'),
                    currentRoom.get('room_ident'),
                    lastUnreadMsg.get('id'),
                ));
        }

        result.unshift(updState);
        return result;
    }

    @on(a.EVENT_MARK_MSG_READ)
    markChatMessagesRead({ newMsgInCurrentDialog, newMsgMarkShown }, state) {
        const result = [];

        const { IS_SUDO_MODE, IS_DEBUG } = Config.data.appState;

        if (IS_SUDO_MODE && !IS_DEBUG) return [state];

        if (newMsgInCurrentDialog && newMsgMarkShown) {
            const currentRoom = getRoom(
                u.getSocketStore(state),
                state.get('openedRoomIdent')
            ).room;
            const lastUnreadMsg = u.findLastUnreadMsg(
                currentRoom.get('history'),
                currentRoom.get('last_read_counter')
            );

            lastUnreadMsg && result.push(
                fx.besidaMarkMsgReadFx(
                    state.get('besida'),
                    state.get('openedRoomIdent'),
                    lastUnreadMsg.get('id'),
                ));
        }
        result.unshift(state);
        return result;
    }

    @on(a.EVENT_CLOSE_DIALOG)
    closeDialog(_, state) {
        const { IS_SUDO_MODE, IS_DEBUG } = Config.data.appState;

        let updState = state;
        const result = [];

        updState = updState.set('visible', false);
        updState = updState.set('theme', null);

        if (updState.get('openedRoomIdent')) {
            updState = updState.set('contextItemId', null);
            updState = updState.set('contextItemType', null);
            updState = updState.set('contextItemPrice', null);
            updState = updState.set('contextItemCurrency', null);
            updState = updState.set('contextDescription', null);
            updState = updState.set('contextMeta', null);
        }

        if (IS_SUDO_MODE && !IS_DEBUG) return [updState];

        if (updState.get('newMsgMarkShown') && updState.get('openedRoomIdent')) {
            const currentRoom = getRoom(
                u.getSocketStore(updState),
                updState.get('openedRoomIdent')
            );

            const lastUnreadMsg = u.findLastUnreadMsg(
                currentRoom.get('history'),
                currentRoom.get('last_read_counter')
            );

            updState = updState.set('newMsgMarkShown', false);

            lastUnreadMsg && result.push(
                fx.besidaMarkMsgReadFx(
                    updState.get('besida'),
                    currentRoom.get('room_ident'),
                    lastUnreadMsg.get('id'),
                ));
        }

        result.unshift(updState);
        return result;
    }

    @on(a.EVENT_BESIDA_CONNECTED)
    setBesidaConnection(b, state) {
        let updState = state;

        const isCompanyTabActive = updState.get('userRole') === BESIDA_ROLE_COMPANY;
        updState = updState.set('besida', b);

        if (isCompanyTabActive && !(
            ~u.sentOrdersStore().indexOf(u.currentOrderId())
            ) && u.getParamByName('room_order_ident') && u.getParamByName('send_msg')) {
            u.sentOrdersStore() && u.addOrderToStore(u.sentOrdersStore(), u.currentOrderId());
            return [updState, fx.besidaSendCtxFx(updState.get('besida'), u.getParamByName('room_order_ident'),
            u.currentOrderId(), 'order', null, null, navigator.userAgent, 'order from chat',
            state.get('contextMeta'), state.get('platform'), state.get('source'), state.get('device'))];
        }
        return [updState];
    }

    @on(a.EVENT_OPEN_ROOM)
    openRoom(roomIdent, state) {
        if (state.getIn(['errors', ERROR_UNIQUE_USERS])) return [state];
        let updState = state;

        const room = getRoom(u.getSocketStore(updState), roomIdent);

        updState = updState.set('currentRoomName', room.get('room_name'));
        updState = updState.set('openedRoomIdent', room.get('room_ident'));
        updState = updState.setIn(['isLoadingData', 'chatChannel'], true);
        updState = updState.set('visible', true);
        return [updState, fx.besidaJoinFx(state.get('besida'), room.get('room_ident'))];
    }

    @on(a.EVENT_OPEN_ROOM_SUCCESS)
    openRoomSuccess(_, state) {
        let updState = state;
        const result = [];

        const isBuyerTabActive = updState.get('userRole') === BESIDA_ROLE_BUYER;

        const currentRoom = getRoom(
            u.getSocketStore(updState),
            updState.get('openedRoomIdent')
        );
        const contextMsgs = currentRoom && (
            !!currentRoom.get('history').size && currentRoom.get('history').filter((msg) => (
                msg.get('context_item_id') && msg.get('context_item_type') === CONTEXT_TYPE_PRODUCT)));
        const lastContxtMsg = contextMsgs && contextMsgs.last();
        // update dialog with new context
        if (isBuyerTabActive && updState.get('contextItemId') && (((
            updState.get('contextItemId') !== (lastContxtMsg && lastContxtMsg.get('context_item_id'))) && (
                updState.get('theme') === currentRoom.get('theme')
            )) || !lastContxtMsg)) {
            result.push(fx.besidaSendCtxFx(
                updState.get('besida'), updState.get('openedRoomIdent'),
                updState.get('contextItemId'), updState.get('contextItemType'),
                updState.get('contextItemPrice'), updState.get('contextItemCurrency'),
                updState.get('contextSource'), updState.get('contextDescription'),
                updState.get('contextMeta'), updState.get('platform'),
                updState.get('source'), updState.get('device'),
            ));
        }
        updState = updState.setIn(['isLoadingData', 'chatChannel'], false);

        const openDialogAnalData = updState.getIn(['analyticsQueue', 'openDialog']);

        if (openDialogAnalData) {
            updState = updState.setIn(['analyticsQueue', 'openDialog'], null);
            result.push(rethrowFx(a.TR_SEND_ANALITICS_ACTION, openDialogAnalData));
        }

        result.unshift(updState);
        return result;
    }

    @on(a.EVENT_BESIDA_SEND_MESSAGE)
    sendMessage({ roomIdent }, state) {
        const { IS_SUDO_MODE, IS_DEBUG } = Config.data.appState;

        let updState = state;
        const result = [];

        if (IS_SUDO_MODE && !IS_DEBUG) return [updState];

        const body = updState.get('chatMsgText').trim();
        if (u.isEmpty(body)) return [updState];
        updState = updState.set('chatMsgText', '');

        // add main action
        result.push(fx.besidaSendMsgFx(
            updState.get('besida'),
            roomIdent,
            body,
            updState.get('platform'),
            updState.get('source'),
            updState.get('device'),
        ));

        result.unshift(updState);
        return result;
    }

    @on(a.EVENT_BESIDA_RETRY_SEND_MESSAGE)
    retrySendMessage({ roomIdent, msgId }, state) {
        const { IS_SUDO_MODE, IS_DEBUG } = Config.data.appState;

        const updState = state;
        const result = [];

        if (IS_SUDO_MODE && !IS_DEBUG) return [updState];
        const currentRoom = getRoom(
            u.getSocketStore(updState),
            updState.get('openedRoomIdent')
        );
        const body = currentRoom.get('history')
            .find((m) => m.get('id') === msgId)
            .get('body')
            .trim();

        if (u.isEmpty(body)) return [updState];

        // add main action
        result.push(fx.besidaRetrySendMsgFx(
            updState.get('besida'),
            roomIdent,
            msgId
        ));

        result.unshift(updState);
        return result;
    }

    @on(a.EVENT_BESIDA_SEND_CONTEXT)
    sendContext({ roomIdent }, state) {
        return [
            state,
            fx.besidaSendCtxFx(
                state.get('besida'), roomIdent, state.get('contextItemId'),
                state.get('contextItemType'), state.get('contextItemPrice'),
                state.get('contextItemCurrency'), state.get('contextSource'),
                state.get('contextDescription'), state.get('contextMeta'),
                state.get('platform'), state.get('source'), state.get('device'),
            ),
        ];
    }

    // improve; don`t try to load history, if last was list[] 0
    @on(a.EVENT_DIALOG_CHECK_FETCH_CONDITION, throttle(500))
    dialogCheckFetchCondition({ inner }, state) {
        let updState = state;
        const result = [];

        const currentChatRoom = getRoom(
            u.getSocketStore(state),
            state.get('openedRoomIdent')
        );

        if (inner <= 60 && !state.getIn(['isLoadingData', 'history']) && (
            (currentChatRoom.get('history') || imm.List()).size >= 30
            )) {
            updState = updState.setIn(['isLoadingData', 'history'], true);
            result.push(rethrowFx(a.EVENT_DIALOG_FETCH_HISTORY));
        }

        result.unshift(updState);
        return result;
    }

    // improve; make smooth render on stayOnThe same scroll
    @on(a.EVENT_DIALOG_FETCH_HISTORY)
    dialogFetchHistory(_, state) {
        let updState = state;
        const currentChatRoom = getRoom(
            u.getSocketStore(updState),
            state.get('openedRoomIdent')
        );

        const lastMsgId = currentChatRoom.get('history').first().get('id');

        updState = updState.set('dialogPrevHeight', state.get('dialogContainer').scrollHeight);

        return [
            updState,
            fx.besidaDialogFetchHistoryFx(
                state.get('besida'),
                state.get('openedRoomIdent'),
                lastMsgId
            )
        ];
    }

    @on(a.EVENT_DIALOG_FETCH_HISTORY_SUCCESS)
    dialogFetchHistorySuccess({ roomIdent, history }, state) {
        let updState = state;

        updState = updState.setIn(['isLoadingData', 'history'], false);

        if (!history) return [updState];

        const currentChatRoom = getRoom(
            u.getSocketStore(updState),
            roomIdent
        );

        updState = updState.set('dialogOldHeight', (currentChatRoom || imm.Map()).get('withContextIn') ? (
            updState.get('dialogContainer').scrollHeight - c.SIZE_DIALOG_CONTEXT_OFFSET * 2) :
        updState.get('dialogContainer').scrollHeight);

        return [
            updState,
            rethrowFx(a.EVENT_DIALOG_STAY_ON_SAME_SCROLL)
        ];
    }

    @on(a.CHAT_CHECK_LIST_LOAD_CONDITION, debounce(500))
    chatCheckListLoadCondition(listContainer, state) {
        const { scrollHeight, offsetHeight, scrollTop } = listContainer;
        const pxToBottom = scrollHeight - offsetHeight - scrollTop;
        const currChatsSize =  h.getFilteredChats(
            (u.getSocketStore(state).get('chats') || imm.Map()).toJS(),
            state.get('chatsStatus'),
            state.get('userRole')
        );

        if (currChatsSize.length < 20) return [state];
        if (u.isNaN(pxToBottom) || pxToBottom > 60) return [state];
        if (state.getIn(['isLoadingData', 'chats'])) return [state];

        // todo; fix that logic, after we will have tabs
        // if (!u.isNull(state.get('besida').lastLoadedChatsSize) && (
        //     state.get('besida').lastLoadedChatsSize < 20)) return [state];

        return [
            state.setIn(['isLoadingData', 'chats'], true),
            fx.besidaChatListLoadFx(
                state.get('besida'),
                state.get('chatsStatus'),
                state.get('userRole'),
            )
        ];
    }

    @on(a.CHAT_LIST_LOAD)
    chatListLoad(_, state) {
        if (state.getIn(['isLoadingData', 'chats'])) return [state];

        const effects = [];
        let updState = state;

        if (u.hasCompanyRole(state)) {
            const companyChats = agg.getFilteredChats(
                u.getSocketStore(state).get('chats'),
                state.get('chatsStatus'),
                BESIDA_ROLE_COMPANY,
            );
            if (!companyChats.size) {
                updState = updState.setIn(['isLoadingData', 'chats'], true);
                effects.push(
                    fx.besidaChatListLoadFx(
                        state.get('besida'),
                        state.get('chatsStatus'),
                        BESIDA_ROLE_COMPANY,
                    )
                );
            }
        }

        const buyerChats =  agg.getFilteredChats(
            u.getSocketStore(state).get('chats'),
            state.get('chatsStatus'),
            BESIDA_ROLE_BUYER,
        );

        if (!buyerChats.size) {
            updState = updState.setIn(['isLoadingData', 'chats'], true);
            effects.push(
                fx.besidaChatListLoadFx(
                    state.get('besida'),
                    state.get('chatsStatus'),
                    BESIDA_ROLE_BUYER,
                )
            );
        }

        // todo; fix that logic, after we will have tabs
        // if (!u.isNull(state.get('besida').lastLoadedChatsSize) && (
        //     state.get('besida').lastLoadedChatsSize < 20)) return [state];

        return [updState, ...effects];
    }

    @on(a.CHAT_LIST_LOAD_SUCCESS)
    chatListLoadSuccess(_, state) {
        return [state.setIn(['isLoadingData', 'chats'], false)];
    }

    @on(a.EVENT_BESIDA_MARK_MSG_READ)
    markMsgRead({ roomIdent, msgId }, state) {
        return [state, fx.besidaMarkMsgReadFx(state.get('besida'), roomIdent, msgId)];
    }

    @on(a.EVENT_BESIDA_CHAT_META_SUCCESS)
    besidaChatMetaSuccess(roomIdent, state) {
        return [u.triggerRerenderForSocketData(state), fx.besidaChatHistoryFx(
            state.get('besida'), roomIdent)];
    }

    @on(a.EVENT_BESIDA_CHAT_HISTORY_SUCCESS)
    besidaChatHistorySuccess(_, state) {
        return [u.triggerRerenderForSocketData(state)];
    }

    @on(a.EVENT_BESIDA_KNOCK_KNOCK)
    knockKnock({ besida, url }, state) {
        url = state.get('autoKnockKnock') || url;
        return [state, fx.besidaKnockKnockFx(besida, url)];
    }

    @on(a.EVENT_BESIDA_HELLO)
    besidaHello(_, state) {
        return [state];
    }

    @on(a.EVENT_BESIDA_SEND_TYPING, throttle(BESIDA_TYPING_DELAY))
    sendTyping(roomIdent, state) {
        return [state, fx.besidaSendTypingFx(state.get('besida'), roomIdent)];
    }

    @on(a.EVENT_AUTO_OPEN_DIALOG)
    autoOpenDialog({ besida }, state) {
        let updState = state;
        const defaultResult = [];
        if (besida && !updState.getIn(['launchOnce', 'openDialogFromMail'])) {
            const openedChats = u.getSocketStore(updState).get('chats') || imm.List();
            const openDialogFromMailObj = u.dialogToOpenFromMail(openedChats);

            // if user links on dialog from mail notation, we should show the direct one
            if (openDialogFromMailObj) {
                updState = updState.set('visible', true);
                updState = updState.setIn(['launchOnce', 'openDialogFromMail'], true);
                defaultResult.push(fx.openRoomFx(openDialogFromMailObj.get('room_ident')));
            }
        }
        defaultResult.unshift(updState);
        return defaultResult;
    }

    @on(a.EVENT_BESIDA_ERROR)
    besidaError({ data }, state) {
        let updState = state;

        updState = updState.set(
            'errors', (data && data.get('errors')) || imm.Map());

        const isFilterErrors = [
            ERROR_TYPE_STOP_WORD,
            ERROR_TYPE_CAPS_LOCK
        ].includes(
            updState.get('errors').keySeq().first()
        );

        if (isFilterErrors) {
            return [updState, rethrowFx(
                a.EVENT_BESIDA_BLOCK_INPUT,
                { unblockDelay: c.DIALOG_WORD_UNBLOCK_INPUT_DELAY }
            )];
        }

        return [updState];
    }

    @on(a.EVENT_BESIDA_MESSAGE)
    besidaMessage({ requestMeta, data }, state) {
        const { IS_SUDO_MODE, IS_DEBUG } = Config.data.appState;

        let updState = state;
        const result = [];

        const receiveRoom = getRoom(
            u.getSocketStore(state), requestMeta
        );

        const lastMsgInCurRoom = receiveRoom && receiveRoom.get('history').last();

        // don`t show new msgs in open dialog as new
        if (requestMeta === state.get('openedRoomIdent') && (
            data.get('user_ident') !== u.getSocketStore(state).get('userId')) &&
            state.get('visible') && !(
                lastMsgInCurRoom && lastMsgInCurRoom.get('context_item_id')
            )) {
            updState = updState.set('newMsgMarkShown', false);
            result.push(
                (IS_SUDO_MODE && !IS_DEBUG) ? () => void 0 : (
                    fx.besidaMarkMsgReadFx(state.get('besida'), requestMeta, data.get('id')))
            );
        }

        // useful comment about analytic
        const historyMsgCnt = receiveRoom.get('history').filter((msg) => !u.isCntxMsg(msg)).size;
        if (historyMsgCnt === 1 && updState.get('userRole') === BESIDA_ROLE_BUYER) {
            result.push(
                rethrowFx(a.TR_SEND_ANALITICS_ACTION, { action: c.ANALCS_ACTION_CUSTOMER_MSG })
            );
        }

        // make several actions after msg was rendered
        result.push(rethrowFx(a.EVENT_BESIDA_MESSAGE_SUCCESS));

        // scroll to last msg if
        const receiveMsg = receiveRoom && receiveRoom.get('history').find(
            (msg) => msg.get('id') === data.get('id')
        );
        const msgStatus = receiveMsg && u.getMessageStatus(updState, receiveMsg);

        ((data.get('user_ident') === u.getSocketStore(updState).get('userId') && (
            msgStatus === MESSAGE_STATUS_SENDING
        )) || (
            u.isOnEndOfScroll(updState.get('dialogContainer')) && (
                data.get('user_ident') !== u.getSocketStore(updState).get('userId')
            ))) && (
            updState = updState.set('goBotOnMsg', true));

        result.unshift(updState);
        return result;
    }

    @on(a.EVENT_BESIDA_MESSAGE_SUCCESS)
    besidaMessageSuccess(_, state) {
        let updState = state;
        // rewrite positions for cntx
        if (updState.get('dialogContainer') && (
            updState.get('dialogPrevHeight') < state.get('dialogContainer').scrollHeight)
        ) updState = updState.set('dialogPrevHeight', updState.get('dialogContainer').scrollHeight);

        return [updState];
    }

    @on(a.EVENT_BESIDA_COUNTERS)
    besidaLattice(_, state) {
        const resultStateFx = [];
        const newChatRooms = u.getSocketStore(state).get('chats').filter(
            (chat) => chat.get('new_chat') && !chat.get('role')
        );
        const chatRoomsWithNewMsg = u.getSocketStore(state).get('chats').filter(
            (chat) => chat.get('isThereNewMsgs') && !chat.get('joined')
        );

        if (newChatRooms.size) {
            newChatRooms.forEach((newChat) => {
                resultStateFx.push(
                    fx.besidaChatMetaFx(
                        state.get('besida'), newChat.get('room_ident')
                    )
                );
            });
        }
        if (chatRoomsWithNewMsg.size) {
            chatRoomsWithNewMsg.forEach((newMsgChat) => {
                resultStateFx.push(
                    fx.besidaChatHistoryFx(
                        state.get('besida'), newMsgChat.get('room_ident'))
                );
            });
        }

        state = u.triggerRerenderForSocketData(state);
        resultStateFx.unshift(state);

        return resultStateFx;
    }

    @on(a.EVENT_BESIDA_REGISTRY)
    besidaRegistry(_, state) {
        return [u.triggerRerenderForSocketData(state)];
    }

    @on(a.EVENT_BESIDA_BAN)
    besidaBan(_, state) {
        return [u.triggerRerenderForSocketData(state)];
    }

    @on(a.EVENT_BESIDA_TYPING)
    besidaTyping({ data: { event } }, state) {
        let updState = state;

        const isOnEndOfScroll = u.isOnEndOfScroll(updState.get('dialogContainer'));
        const startTypingEvent = event === BESIDA_EVENT_START_TYPING;

        (isOnEndOfScroll && startTypingEvent) && (
            updState = updState.withMutations((stateMutate) => {
                stateMutate.set('goBotOnMsg', true);
                stateMutate.set('goBotOnTyping', true);
            })
        );
        return [u.triggerRerenderForSocketData(updState)];
    }


    @on(a.EVENT_BESIDA_MARK_READ)
    besidaMarkRead(_, state) {
        return [u.triggerRerenderForSocketData(state)];
    }

    @on(a.EVENT_BESIDA_ROOM)
    besidaRoom(_, state) {
        return [u.triggerRerenderForSocketData(state)];
    }
}
